import {Button} from '@wandb/weave/components/Button';
import React, {FC, useCallback, useState} from 'react';
import {toast} from 'react-toastify';

import {
  OrganizationSubscriptionInfoQuery,
  PlanType,
  useUpdateOrganizationSubscriptionSeatsMutation,
} from '../../generated/graphql';
import {Analytics} from '../../services/analytics';
import {
  isPrimaryStripePlanInfoWithUnitPrice,
  useGetMinimumModelsSeatsToPurchase,
  useGetSubscriptionPlanByName,
} from '../../util/accounts/pricing';
import {getErrMsgFromError, propagateErrorsContext} from '../../util/errors';
import {SeatControl} from './Checkout/TeamsPlanTab';
import {TermsAndPrivacyCheckbox} from './Checkout/TermsAndPrivacyCheckbox';
import * as S from './TeamsPurchaseSeatModal.styles';
import {PlanName} from './util';

export type TeamsPurchaseSeatModalProps = {
  modelsSeatsSubscription: NonNullable<
    OrganizationSubscriptionInfoQuery['organization']
  >['subscriptions'][number];
  organizationId: string;
  organizationName: string;
  proposedSeats?: number;
  onConfirm: () => Promise<void>;
  onClose(): void;
};

export const TeamsPurchaseSeatModal: FC<TeamsPurchaseSeatModalProps> = ({
  modelsSeatsSubscription,
  organizationId,
  organizationName,
  proposedSeats,
  onConfirm,
  onClose,
}) => {
  const initialSeats = proposedSeats ?? modelsSeatsSubscription.seats;
  // confirmClicked is used to prevent double-clicking the confirm button
  // and to show the pending state.
  const {loading, plans} = useGetSubscriptionPlanByName();
  const [isConfirmClicked, setIsConfirmClicked] = useState(false);
  const [seats, setSeats] = useState(initialSeats);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [updateOrganizationSubscriptionSeats] =
    useUpdateOrganizationSubscriptionSeatsMutation({
      context: propagateErrorsContext(),
    });

  const {minModelsSeatsToPurchase} = useGetMinimumModelsSeatsToPurchase(
    organizationName,
    modelsSeatsSubscription.seats
  );

  const handleConfirm = useCallback(async () => {
    setIsConfirmClicked(true);
    const location = 'teams purchase seats modal';
    Analytics.track('Organization Seats Update Confirmed', {
      seats,
      location,
    });
    try {
      const updateSeatsResult = await updateOrganizationSubscriptionSeats({
        variables: {
          organizationID: organizationId,
          seats,
        },
      });

      const errors = updateSeatsResult.errors;
      const isSuccess =
        updateSeatsResult.data?.updateOrganizationSubscriptionSeats?.success ===
        true;

      if (errors != null) {
        const errMsg = errors.map(e => e.message).join(', ');
        throw new Error(errMsg);
      }
      if (!isSuccess) {
        throw new Error('Update failed');
      }
      Analytics.track('Organization Seats Updated', {
        location,
        seats,
        organizationID: organizationId,
      });

      onConfirm();
      toast('Succesfully added seats.');
    } catch (err) {
      const errMsg = getErrMsgFromError(err);
      toast(`Error adding seats: ${errMsg ? `: ${errMsg}` : ``}`);
      Analytics.track('Organization Seats Update Error Viewed', {
        organizationId,
        location: 'teams purchase seats modal',
        error: errMsg,
      });
    }
    setIsConfirmClicked(false);
    onClose();
  }, [
    seats,
    onClose,
    updateOrganizationSubscriptionSeats,
    organizationId,
    onConfirm,
  ]);

  if (loading || minModelsSeatsToPurchase == null) {
    return null;
  }

  const monthlyTeamPlan = plans[PlanName.MonthlyTeams2023];
  const yearlyTeamPlan = plans[PlanName.YearlyTeams2023];

  if (
    monthlyTeamPlan == null ||
    !isPrimaryStripePlanInfoWithUnitPrice(monthlyTeamPlan) ||
    yearlyTeamPlan == null ||
    !isPrimaryStripePlanInfoWithUnitPrice(yearlyTeamPlan)
  ) {
    return null;
  }

  const isYearlyModelsSub =
    modelsSeatsSubscription.plan.planType === PlanType.ModelsSeats ||
    modelsSeatsSubscription.plan.id === yearlyTeamPlan.id;

  const isDisabled =
    isConfirmClicked ||
    !isTermsChecked ||
    seats === modelsSeatsSubscription.seats;

  return (
    <S.Modal open size="small" onClose={onClose}>
      <S.Header>Add seats</S.Header>
      <S.Content>
        <SeatControl
          minSeats={minModelsSeatsToPurchase}
          // Temporarily use yearly teams plan to show total cost for seat upgrade on Pro Plan
          // Todo (mena-david): Update checkout price to use Stripe Preview invoice feature
          proposedPlan={isYearlyModelsSub ? yearlyTeamPlan : monthlyTeamPlan}
          seats={seats}
          setSeats={setSeats}
        />
      </S.Content>
      <TermsAndPrivacyCheckbox
        isTermsChecked={isTermsChecked}
        setIsTermsChecked={setIsTermsChecked}
      />
      {isConfirmClicked && (
        <S.PendingMessage positive>Adding seats...</S.PendingMessage>
      )}
      <S.ButtonGroup>
        <Button
          disabled={isDisabled}
          data-test="confirm-purchase-seat"
          onClick={handleConfirm}>
          Update Seats
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Not now
        </Button>
      </S.ButtonGroup>
    </S.Modal>
  );
};
